import PropTypes from 'prop-types';
import { IconButton, TextField, Typography } from '@material-ui/core';
import MaterialGrid from '@material-ui/core/Grid';
import PrimaryButton from 'common/components/buttons/primaryButton';
import SecondaryButton from 'common/components/buttons/secondaryButton';
import { InputLabelOutlined } from 'common/components/fields/InputLabelOutlined';
import LoadingProgress from 'common/components/progress/loading';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, FieldArray, Form, getFormValues, reduxForm } from 'redux-form';
import GenericConfirmationCard from 'common/components/cards/genericConfirmationModel/genericConfirmationModel';
import { isMobile } from 'react-device-detect';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

const MAX_SERIES = 29;

const padSeries = (v) => String(v).padStart(2, '0');

const MonthPickerField = ({ input, meta }) => (
  <TextField
    type="month"
    label="Mês/Ano"
    value={input.value || ''}
    onChange={input.onChange}
    variant="outlined"
    size="small"
    fullWidth
    InputLabelProps={{ shrink: true }}
    error={meta.touched && !!meta.error}
    helperText={meta.touched && meta.error}
  />
);

const renderScheduleRows = ({ fields }) => (
  <div>
    <MaterialGrid container spacing={2} style={{ marginBottom: 8 }}>
      <MaterialGrid item xs={5}>
        <Typography variant="subtitle2">Mês/Ano</Typography>
      </MaterialGrid>
      <MaterialGrid item xs={4}>
        <Typography variant="subtitle2">Quantidade</Typography>
      </MaterialGrid>
      <MaterialGrid item xs={3} />
    </MaterialGrid>
    {fields.map((member, index) => (
      <MaterialGrid container spacing={2} key={index} alignItems="center">
        <MaterialGrid item xs={5}>
          <Field component={MonthPickerField} name={`${member}.month`} />
        </MaterialGrid>
        <MaterialGrid item xs={4}>
          <Field
            component={InputLabelOutlined}
            name={`${member}.quantity`}
            label="Qtd"
            type="number"
            margin="dense"
          />
        </MaterialGrid>
        <MaterialGrid item xs={3}>
          <IconButton onClick={() => fields.remove(index)} size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </MaterialGrid>
      </MaterialGrid>
    ))}
    <PrimaryButton
      type="button"
      onClick={() => fields.push({ month: '', quantity: 1 })}
      style={{ marginTop: 8, textTransform: 'none' }}
      startIcon={<AddIcon />}>
      Adicionar mês
    </PrimaryButton>
  </div>
);

/**
 * Sugere a próxima faixa de séries quando admin adiciona nova casa:
 *   - 1ª casa → 00-09, prizeIndex 1
 *   - 2ª casa → 10-19, prizeIndex 2
 *   - 3ª casa → 20-29, prizeIndex 3
 *   - 4ª+ casa → sem sugestão de faixa (admin define), prizeIndex próximo livre (1-5)
 */
const suggestNextHouseDefaults = (existingHouses) => {
  const count = existingHouses.length;
  if (count < 3) {
    return {
      name: '',
      seriesStart: count * 10,
      seriesEnd: count * 10 + 9,
      prizeIndex: count + 1,
    };
  }
  const usedPrizes = new Set(
    existingHouses.map((h) => parseInt(h?.prizeIndex, 10)).filter(Boolean)
  );
  let nextPrizeIndex = 1;
  while (usedPrizes.has(nextPrizeIndex) && nextPrizeIndex <= 5) {
    nextPrizeIndex += 1;
  }
  return {
    name: '',
    seriesStart: '',
    seriesEnd: '',
    prizeIndex: nextPrizeIndex <= 5 ? nextPrizeIndex : 1,
  };
};

const renderHousesRows = ({ fields, currentHouses }) => {
  const houses = currentHouses || [];

  // Total de séries atribuídas + warnings
  const totalSeries = houses.reduce((sum, h) => {
    const s = parseInt(h?.seriesStart, 10);
    const e = parseInt(h?.seriesEnd, 10);
    if (!Number.isInteger(s) || !Number.isInteger(e) || s > e) return sum;
    return sum + (e - s + 1);
  }, 0);
  const unused = Math.max(0, 30 - totalSeries);

  // Validação visual de sobreposição e prizeIndex duplicado
  const overlaps = [];
  const prizeIndexUsage = {};
  houses.forEach((h, i) => {
    const s = parseInt(h?.seriesStart, 10);
    const e = parseInt(h?.seriesEnd, 10);
    const p = parseInt(h?.prizeIndex, 10);
    if (Number.isInteger(p)) {
      prizeIndexUsage[p] = (prizeIndexUsage[p] || 0) + 1;
    }
    if (!Number.isInteger(s) || !Number.isInteger(e)) return;
    houses.forEach((other, j) => {
      if (i >= j) return;
      const s2 = parseInt(other?.seriesStart, 10);
      const e2 = parseInt(other?.seriesEnd, 10);
      if (!Number.isInteger(s2) || !Number.isInteger(e2)) return;
      if (s <= e2 && s2 <= e) overlaps.push([i, j]);
    });
  });
  const duplicatePrizes = Object.entries(prizeIndexUsage)
    .filter(([, count]) => count > 1)
    .map(([p]) => p);

  return (
    <div>
      <MaterialGrid container spacing={2} style={{ marginBottom: 8 }}>
        <MaterialGrid item xs={4}>
          <Typography variant="subtitle2">Nome da casa</Typography>
        </MaterialGrid>
        <MaterialGrid item xs={2}>
          <Typography variant="subtitle2">Série início</Typography>
        </MaterialGrid>
        <MaterialGrid item xs={2}>
          <Typography variant="subtitle2">Série fim</Typography>
        </MaterialGrid>
        <MaterialGrid item xs={2}>
          <Typography variant="subtitle2">Prêmio LF</Typography>
        </MaterialGrid>
        <MaterialGrid item xs={2} />
      </MaterialGrid>

      {fields.map((member, index) => {
        const h = houses[index] || {};
        const start = parseInt(h.seriesStart, 10);
        const end = parseInt(h.seriesEnd, 10);
        const rangeLabel =
          Number.isInteger(start) && Number.isInteger(end) && start <= end
            ? `${padSeries(start)}–${padSeries(end)} · ${
                (end - start + 1) * 100000
              } números potenciais`
            : 'Faixa não definida';
        const hasOverlap = overlaps.some((pair) => pair[0] === index || pair[1] === index);
        return (
          <div key={index} style={{ marginBottom: 12 }}>
            <MaterialGrid container spacing={2} alignItems="center">
              <MaterialGrid item xs={4}>
                <Field
                  component={InputLabelOutlined}
                  name={`${member}.name`}
                  label={`Casa ${index + 1}`}
                  margin="dense"
                />
              </MaterialGrid>
              <MaterialGrid item xs={2}>
                <Field
                  component={InputLabelOutlined}
                  name={`${member}.seriesStart`}
                  label="Início"
                  type="number"
                  margin="dense"
                />
              </MaterialGrid>
              <MaterialGrid item xs={2}>
                <Field
                  component={InputLabelOutlined}
                  name={`${member}.seriesEnd`}
                  label="Fim"
                  type="number"
                  margin="dense"
                />
              </MaterialGrid>
              <MaterialGrid item xs={2}>
                <Field
                  component={InputLabelOutlined}
                  name={`${member}.prizeIndex`}
                  label="Prêmio (1-5)"
                  type="number"
                  margin="dense"
                />
              </MaterialGrid>
              <MaterialGrid item xs={2}>
                <IconButton onClick={() => fields.remove(index)} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </MaterialGrid>
            </MaterialGrid>
            <Typography
              variant="caption"
              style={{
                marginLeft: 8,
                color: hasOverlap ? '#d32f2f' : '#666',
              }}>
              {hasOverlap ? 'Faixa sobrepõe outra casa — ajuste os limites.' : rangeLabel}
            </Typography>
          </div>
        );
      })}

      <PrimaryButton
        type="button"
        onClick={() => fields.push(suggestNextHouseDefaults(houses))}
        style={{ marginTop: 8, textTransform: 'none' }}
        startIcon={<AddIcon />}>
        Adicionar casa
      </PrimaryButton>

      {houses.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {duplicatePrizes.length > 0 && (
            <Typography
              variant="body2"
              style={{
                color: '#d32f2f',
                backgroundColor: '#ffebee',
                padding: '8px 12px',
                borderRadius: 4,
                marginBottom: 8,
              }}>
              ⚠ Prêmios LF duplicados: {duplicatePrizes.join(', ')}. Cada casa precisa usar um
              prêmio distinto (1-5).
            </Typography>
          )}
          {overlaps.length > 0 && (
            <Typography
              variant="body2"
              style={{
                color: '#d32f2f',
                backgroundColor: '#ffebee',
                padding: '8px 12px',
                borderRadius: 4,
                marginBottom: 8,
              }}>
              ⚠ Há sobreposição entre faixas de séries. Ajuste antes de salvar.
            </Typography>
          )}
          {unused > 0 && (
            <Typography
              variant="body2"
              style={{
                color: '#e65100',
                backgroundColor: '#fff3e0',
                padding: '8px 12px',
                borderRadius: 4,
              }}>
              ℹ {unused} série{unused === 1 ? '' : 's'} não atribuída
              {unused === 1 ? '' : 's'} — até {(unused * 100000).toLocaleString('pt-BR')} números
              potenciais não serão usados.
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

class Interaction extends Component {
  onSubmit = (values) => {
    if (values.categories_field) {
      values.categories = values.categories_field.map((item) => item.id);
    }
    if (values.groupings_field) {
      values.groupings = values.groupings_field.map((item) => item.id);
    }

    const rawHouses = values.houses_field || [];

    // Validação client-side antes do submit
    const houses = rawHouses.map((h, i) => ({
      name: (h?.name || '').trim(),
      seriesStart: parseInt(h?.seriesStart, 10),
      seriesEnd: parseInt(h?.seriesEnd, 10),
      prizeIndex: parseInt(h?.prizeIndex, 10),
      _idx: i + 1,
    }));

    // Permite zero casas (campanha sem segmentação por casa)
    if (houses.length > 0) {
      const names = houses.map((h) => h.name);
      if (names.some((n) => !n)) {
        // eslint-disable-next-line no-alert
        alert('Todas as casas precisam de um nome.');
        return;
      }
      if (new Set(names).size !== names.length) {
        // eslint-disable-next-line no-alert
        alert('Há nomes de casa duplicados. Corrija antes de continuar.');
        return;
      }
      for (const h of houses) {
        if (
          !Number.isInteger(h.seriesStart) ||
          !Number.isInteger(h.seriesEnd) ||
          h.seriesStart < 0 ||
          h.seriesEnd > MAX_SERIES ||
          h.seriesStart > h.seriesEnd
        ) {
          // eslint-disable-next-line no-alert
          alert(`Casa "${h.name}": faixa de séries inválida. Use valores 00-29 com início ≤ fim.`);
          return;
        }
        if (!Number.isInteger(h.prizeIndex) || h.prizeIndex < 1 || h.prizeIndex > 5) {
          // eslint-disable-next-line no-alert
          alert(`Casa "${h.name}": prêmio LF precisa ser um número de 1 a 5.`);
          return;
        }
      }
      // Sobreposição
      const sorted = [...houses].sort((a, b) => a.seriesStart - b.seriesStart);
      for (let i = 1; i < sorted.length; i += 1) {
        if (sorted[i].seriesStart <= sorted[i - 1].seriesEnd) {
          // eslint-disable-next-line no-alert
          alert(
            `Sobreposição entre "${sorted[i - 1].name}" e "${
              sorted[i].name
            }". Ajuste antes de salvar.`
          );
          return;
        }
      }
      // PrizeIndex único
      const seenPrizes = new Set();
      for (const h of houses) {
        if (seenPrizes.has(h.prizeIndex)) {
          // eslint-disable-next-line no-alert
          alert(`Prêmio LF duplicado: ${h.prizeIndex}. Cada casa precisa usar um prêmio distinto.`);
          return;
        }
        seenPrizes.add(h.prizeIndex);
      }
    }

    values.houses = houses.map(({ _idx, ...h }) => h);
    values.prizes_per_house = parseInt(values.prizes_per_house, 10) || 1;
    values.submitFromButton = true;
    this.props.onClickProgress(values);
  };

  render() {
    const atividade = this?.props?.formValues;
    const now = new Date();
    const isDisabled = !atividade?.is_draft && now > new Date(atividade?.end);
    const currentHouses = atividade?.houses_field || [];

    return this.props.referralCampaign.loading ? (
      <div className="d-flex justify-content-center mt-5">
        <LoadingProgress />
      </div>
    ) : (
      <>
        <Form role="form" onSubmit={this.props.handleSubmit(this.onSubmit)} noValidate>
          <div
            style={{
              overflowY: 'scroll',
              height: isMobile ? 'calc(100vh - 250px)' : 'calc(100vh - 355px)',
              overflowX: 'hidden',
              paddingRight: isMobile ? 0 : 15,
            }}>
            <MaterialGrid container spacing={3}>
              <MaterialGrid item xs={12}>
                <Typography variant="h6" style={{ marginBottom: 8 }}>
                  Números da Sorte por Mês
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                  Configure quantos números da sorte serão gerados por indicação em cada mês da
                  campanha. O último mês configurado será usado para meses subsequentes.
                </Typography>
                <FieldArray name="lucky_numbers_schedule" component={renderScheduleRows} />
              </MaterialGrid>

              <MaterialGrid item xs={12}>
                <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
                  Casas da Atividade
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                  Cada casa recebe uma faixa de séries (de 00 a 29, total de 30 séries) e um prêmio
                  da Loteria Federal (de 1 a 5) para o cálculo da série base no sorteio. O indicador
                  escolherá uma das casas no formulário de indicação. Casas com indicações
                  vinculadas não podem ter faixa ou prêmio alterados.
                </Typography>
                <FieldArray
                  name="houses_field"
                  component={renderHousesRows}
                  props={{ currentHouses }}
                />
              </MaterialGrid>

              <MaterialGrid item xs={12}>
                <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
                  Prêmios por Casa
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                  Quantidade de ganhadores que cada casa terá em cada sorteio realizado.
                </Typography>
                <Field
                  component={InputLabelOutlined}
                  name="prizes_per_house"
                  label="Prêmios por casa"
                  type="number"
                  margin="dense"
                />
              </MaterialGrid>

              <MaterialGrid item xs={12}>
                <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
                  Sorteio Final (opcional)
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                  Defina a data planejada do sorteio final da campanha. Quando essa data for
                  alcançada, o tipo de sorteio &quot;final&quot; será pré-selecionado
                  automaticamente no painel de execução.
                </Typography>
                <Field
                  component={InputLabelOutlined}
                  name="final_draw_date"
                  label="Data do sorteio final"
                  type="datetime-local"
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                />
              </MaterialGrid>

              <MaterialGrid item xs={12}>
                <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
                  Meios de Compartilhamento
                </Typography>
                <MaterialGrid container spacing={2}>
                  <MaterialGrid item xs={6}>
                    <Field
                      component={({ input }) => (
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!input.value}
                            onChange={input.onChange}
                            style={{ marginRight: 8 }}
                          />
                          E-mail
                        </label>
                      )}
                      name="share_channels.email"
                    />
                  </MaterialGrid>
                  <MaterialGrid item xs={6}>
                    <Field
                      component={({ input }) => (
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={!!input.value}
                            onChange={input.onChange}
                            style={{ marginRight: 8 }}
                          />
                          WhatsApp
                        </label>
                      )}
                      name="share_channels.whatsapp"
                    />
                  </MaterialGrid>
                </MaterialGrid>
              </MaterialGrid>

              <MaterialGrid item xs={12}>
                <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
                  Regulamento
                </Typography>
                <Field
                  component={InputLabelOutlined}
                  label="Regulamento da campanha"
                  name="regulation"
                  multiline
                  rows={6}
                  margin="normal"
                />
              </MaterialGrid>
            </MaterialGrid>

            <GenericConfirmationCard
              open={this.props.openConfirmation}
              cancelClose={this.props.handleCancelClose}
              maxWidthFixed
              labelLeftButton={'Continuar aqui'}
              labelRightButton={this.props.buttonConfirmationLabel}
              general={this.props.general}
              confirmButtonColor={'primary'}
              title={this.props.confirmationTitle}
              text={this.props.confirmationSubtitle}
              onClose={() => this.props.setOpenConfirmation(false)}
              onClick={() => this.props.router.push('/owner/acoes/minhas-atividades')}
            />
          </div>
          <MaterialGrid
            container
            display="flex"
            justify="space-around"
            alignItems="center"
            style={{
              margin: '25px 0px',
              overflowX: 'hidden',
              paddingRight: isMobile ? 0 : 29,
            }}>
            <SecondaryButton
              type="button"
              color="secondary"
              onClick={this.props.prevClick}
              style={{
                margin: 0,
                width: '48%',
                padding: '10px 20px',
                textTransform: 'none',
              }}>
              Voltar
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={this.props.activeActions.buttonLoading || isDisabled}
              color="primary"
              style={{
                margin: 0,
                width: '48%',
                padding: isMobile ? '12px 20px' : '10px 20px',
                textTransform: 'none',
                fontSize: isMobile ? 12 : undefined,
              }}>
              Salvar e continuar
            </PrimaryButton>
          </MaterialGrid>
        </Form>
      </>
    );
  }
}

Interaction.propTypes = {
  activeActions: PropTypes.shape({ buttonLoading: PropTypes.any }),
  auth: PropTypes.shape({ user: PropTypes.shape({ establishments: PropTypes.any }) }),
  buttonConfirmationLabel: PropTypes.any,
  confirmationSubtitle: PropTypes.any,
  confirmationTitle: PropTypes.any,
  general: PropTypes.any,
  handleCancelClose: PropTypes.any,
  handleSubmit: PropTypes.func,
  onClickProgress: PropTypes.func,
  openConfirmation: PropTypes.any,
  prevClick: PropTypes.any,
  router: PropTypes.shape({
    params: PropTypes.shape({ action: PropTypes.string }),
    push: PropTypes.func,
  }),
  setOpenConfirmation: PropTypes.func,
  referralCampaign: PropTypes.shape({ loading: PropTypes.any }),
  formValues: PropTypes.any,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  referralCampaign: state.referralCampaign,
  activeActions: state.activeActions,
  general: state.general,
  formValues: getFormValues('referralCampaignForm')(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'referralCampaignForm',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  })(Interaction)
);
